import { Button, Offscreen } from '@deque/cauldron-react';
import React, { type ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type HttpError } from '~/common/api-client';
import { type Resource } from '~/common/components/Link';
import {
  AXE_MCP_SERVER_DOCKER_HUB_URL,
  AXE_MCP_SERVER_DOCS_URL,
  AXE_MCP_SERVER_NPM_URL,
  PRODUCT_URLS,
  ProductSlugs
} from '~/common/constants';
import { useAuthContext } from '~/common/contexts/auth';
import { useGlobalToast } from '~/common/contexts/globalToast';
import billingClientV2 from '~/common/utils/billing-client/client-v2';
import styles from './AvailableProducts.css';
import EnterpriseAdminList from './EnterpriseAdminList';
import ProductCard from './V2ProductCard';

export interface AxeMCPServerCardProps {
  /**
   * Billing product id for `axe-devtools-html`. The card is MCP-branded but the free
   * plan it activates is a tier of Axe DevTools for Web.
   */
  htmlProductId: string;
  enterpriseId: string;
  isAdmin: boolean;
  /** `awaitingAccess`: the enterprise holds the `free` plan but the user has no seat on it. */
  status: 'activatable' | 'awaitingAccess' | 'active';
  enterpriseAdmins?: string[];
}

const GETTING_STARTED_URL =
  PRODUCT_URLS[ProductSlugs.axeMCPServer].gettingStarted;

const AxeMCPServerCard = ({
  htmlProductId,
  enterpriseId,
  isAdmin,
  status,
  enterpriseAdmins = []
}: AxeMCPServerCardProps): ReactElement => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const { setContents } = useGlobalToast();
  const [activating, setActivating] = useState(false);

  const resources: Resource[] = [
    {
      name: t('Axe MCP Server Docs'),
      url: AXE_MCP_SERVER_DOCS_URL,
      openInNewTab: true
    },
    {
      name: t('Axe MCP Server on npm'),
      url: AXE_MCP_SERVER_NPM_URL,
      openInNewTab: true
    },
    {
      name: t('Axe MCP Server on Docker Hub'),
      url: AXE_MCP_SERVER_DOCKER_HUB_URL,
      openInNewTab: true
    },
    {
      name: t('Get started with the Axe MCP Server'),
      url: GETTING_STARTED_URL
    }
  ];

  const activate = async () => {
    if (activating) {
      return;
    }
    setActivating(true);

    try {
      await billingClientV2.enterprises.createFreeSubscription(
        htmlProductId,
        enterpriseId,
        user?.token as string
      );
    } catch (error) {
      // A 409 means the subscription already exists, which is the end state we were
      // asking for, so treat it as success and redirect anyway.
      if ((error as HttpError).status !== 409) {
        setContents(
          t('Failed to activate the Axe MCP Server free plan.'),
          'error'
        );
        setActivating(false);
        return;
      }
    }

    // A hard navigation, not `history.push`: billing state is not refreshed on a
    // router push, so the enterprise's subscriptions would stay stale.
    window.location.assign('/user-access');
  };

  return (
    <ProductCard
      title="Axe MCP Server"
      subtitle={t('Free plan (limited functionality)')}
      description={t(
        'Catch accessibility issues while you code, straight from your AI coding agent in Cursor, Claude Code, VS Code, and more. Powered by Axe-core, the accessibility engine downloaded over 5 billion times.'
      )}
      resources={resources}
    >
      {status === 'awaitingAccess' && (
        <EnterpriseAdminList
          message={t(
            'Ask your enterprise admin to give you access to this plan:'
          )}
          enterpriseAdmins={enterpriseAdmins}
        />
      )}
      {status === 'activatable' &&
        (isAdmin ? (
          <div className={styles.ctaButtonWrap}>
            <div className={styles.ctaButtonContainer}>
              <Button
                variant="secondary"
                className={styles.ctaButton}
                aria-disabled={activating}
                onClick={activate}
              >
                {t('Activate Free Plan')}
                <Offscreen> Axe MCP Server</Offscreen>
              </Button>
            </div>
          </div>
        ) : (
          <EnterpriseAdminList
            message={t(
              'Ask your enterprise admin to activate this plan for your organization:'
            )}
            enterpriseAdmins={enterpriseAdmins}
          />
        ))}
    </ProductCard>
  );
};

export default AxeMCPServerCard;
